import { createContext, PropsWithChildren, useMemo, useState } from "react";
import { performLogout, performOidcLogout } from "lib/SpringAxios";
import { legacyLogout } from "hooks/UserHooks";
import AuthService from "utils/authService";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

export interface AuthenticatedUser {
  personId: string;
  legacyUserId: number;
  superUser: boolean;
}

export interface AuthenticationContextProps {
  authenticated: boolean;
  user?: AuthenticatedUser;
  logout: () => Promise<void>;
  login: (user: AuthenticatedUser) => void;
}

export const AuthenticationContext = createContext<AuthenticationContextProps>({
  authenticated: false,
  login: () => {
    throw new Error("Must be used within an authentication context");
  },
  logout: () => {
    throw new Error("Must be used within an authentication context");
  },
});

export function AuthenticationProvider(props: PropsWithChildren) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<AuthenticatedUser | undefined>();
  const queryClient = useQueryClient();

  const authenticationContextValue: AuthenticationContextProps = useMemo(() => {
    return {
      authenticated: isLoggedIn,
      user,
      logout: async () => {
        // First, while the session this request authenticates with is still valid: it ends every
        // Zitadel session this person holds, which is what stops the login page below signing them
        // straight back in without a prompt.
        const logoutUrl = await performOidcLogout();

        await legacyLogout().catch(() => {
          /* No op, ignore logout errors, probably caused by being already logged out */
        });
        await performLogout().catch(() => {
          /* No op, ignore logout errors, probably caused by being already logged out */
        });

        AuthService.discardJwt();
        setIsLoggedIn(false);
        setUser(undefined);
        queryClient.clear();
        if (Sentry.isEnabled()) {
          Sentry.setUser(null);
        }

        sessionStorage.clear();

        // Last, after every trace of the session is gone. Leaving for the provider first would
        // leave this application's own state behind, and the page would come back from the logout
        // redirect still believing it was signed in.
        if (logoutUrl && !logoutUrl.includes(globalThis.location.origin)) {
          // replace, not assign: the signed-in page must not be one Back button away.
          globalThis.location.replace(logoutUrl);
        }
      },
      login: (user: AuthenticatedUser) => {
        setIsLoggedIn(true);
        setUser(user);
        if (Sentry.isEnabled()) {
          Sentry.setUser({
            id: user.personId,
          });
        }
      },
    };
  }, [isLoggedIn, user]);

  return (
    <AuthenticationContext.Provider value={authenticationContextValue}>
      {props.children}
    </AuthenticationContext.Provider>
  );
}
